import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { Router } from '@angular/router';
import { UserRole } from '../../../models/auth.model';
import { AuthService } from '../../../../core/services/auth.service';
import { ModalType } from '../modal/modal.component';
import { ModalComponent } from '../modal/modal.component';
import { ActionMenuStateService } from '../../../services/action-menu-state.service';
import { Subscription } from 'rxjs';
import { MessageService } from 'primeng/api';
import { RoutingSlipService } from '../../../services/routing-slip.service';
import {
  isEarlierRoutingLevelPending,
  WAIT_FOR_PREVIOUS_USER_MESSAGE,
} from '../../../utils/routing-slip-turn.util';

@Component({
  selector: 'app-action-menu',
  standalone: true,
  imports: [CommonModule, ButtonModule, MenuModule, ModalComponent],
  templateUrl: './action-menu.component.html',
})
export class ActionMenuComponent implements OnInit, OnDestroy {
  @Input() row: any;
  @Input() context: string = 'default'; // 'default', 'users', etc.
  @Input() getPermissionForRow?: (row: any) => number; // Function to get permission for a row
  @ViewChild('menu') menu!: any;

  @Output() view = new EventEmitter<any>();
  @Output() approve = new EventEmitter<any>();
  @Output() reject = new EventEmitter<any>();
  @Output() auditLog = new EventEmitter<any>();
  @Output() comment = new EventEmitter<any>();
  @Output() viewClick = new EventEmitter<any>();
  @Output() edit = new EventEmitter<any>();
  @Output() delete = new EventEmitter<any>();
  @Output() reply = new EventEmitter<any>();

  currentUser: any;
  items: MenuItem[] = [];

  showModal: boolean = false;
  modalType: ModalType = 'comment';
  modalTitle: string = '';

  // Menu state management
  private menuId: string = '';
  private subscription: Subscription = new Subscription();
  private isMenuVisible: boolean = false;

  constructor(
    private authService: AuthService,
    private router: Router,
    private actionMenuStateService: ActionMenuStateService,
    private routingSlipService: RoutingSlipService,
    private messageService: MessageService
  ) {
    // Generate unique menu ID for this instance
    this.menuId = `menu_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;
  }

  ngOnInit(): void {
    // ✅ fix: assign with this.
    this.currentUser = this.authService.getCurrentUser();

    // Subscribe to menu state changes
    this.subscription.add(
      this.actionMenuStateService.activeMenuId$.subscribe((activeMenuId) => {
        if (activeMenuId !== this.menuId && this.isMenuVisible) {
          // Another menu is active, close this one
          this.closeMenu();
        }
      })
    );

    // ✅ menu items based on context
    if (this.context === 'users') {
      // For users table - only EDIT and DELETE
      this.items = [
        {
          label: 'Edit',
          icon: 'pi pi-file-edit',
          command: () => {
            this.onMenuItemClick();
            this.edit.emit(this.row);
          },
        },
        {
          label: 'Archive',
          icon: 'pi pi-trash',
          command: () => {
            this.onMenuItemClick();
            this.delete.emit(this.row);
          },
        },
      ];
    } else if (this.context === 'letters') {
      // Letters specific menu items based on user role and approveRejectStatus
      if (this.currentUser?.role === UserRole.ADMIN) {
        // ADMIN: Show View, Reply, Edit, and Audit Log
        this.items = [
          {
            label: 'View',
            icon: 'pi pi-eye',
            command: () => {
              this.onMenuItemClick();
              this.viewClick.emit(this.row);
            },
          },
          {
            label: 'Reply',
            icon: 'pi pi-reply',
            command: () => {
              this.onMenuItemClick();
              this.reply.emit(this.row);
            },
          },
          {
            label: 'Edit',
            icon: 'pi pi-file-edit',
            command: () => {
              this.onMenuItemClick();
              this.edit.emit(this.row);
            },
          },
          {
            label: 'Audit Log',
            icon: 'pi pi-list',
            command: () => {
              this.onMenuItemClick();
              this.auditLog.emit(this.row);
            },
          },
        ];
      } else if (this.currentUser?.role === UserRole.SECRETARY) {
        // SECRETARY: Show View, Edit, Reply, and Audit Log (NO Approve/Reject)
        // Get approveRejectStatus from row data
        const approveRejectStatus = this.row?.approveRejectStatus || 3; // Default to 3 (pending)
        
        // Base items that are always shown
        this.items = [
          {
            label: 'View',
            icon: 'pi pi-eye',
            command: () => {
              this.onMenuItemClick();
              this.viewClick.emit(this.row);
            },
          },
          {
            label: 'Reply',
            icon: 'pi pi-reply',
            command: () => {
              this.onMenuItemClick();
              this.reply.emit(this.row);
            },
          },
          {
            label: 'Audit Log',
            icon: 'pi pi-list',
            command: () => {
              this.onMenuItemClick();
              this.auditLog.emit(this.row);
            },
          },
        ];

        // Add Edit option if not rejected
        if (approveRejectStatus !== 2) {
          this.items.splice(2, 0, // Insert before Audit Log
            {
              label: 'Edit',
              icon: 'pi pi-file-edit',
              command: () => {
                this.onMenuItemClick();
                this.edit.emit(this.row);
              },
            }
          );
        }
      } else if (this.currentUser?.role === UserRole.MANAGER) {
        // Manager: Keep existing behavior - no action menu, just Reply button
        this.items = [];
      } else {
        // Default fallback for other roles in letters context - only View
        this.items = [
          {
            label: 'View',
            icon: 'pi pi-eye',
            command: () => {
              this.onMenuItemClick();
              this.viewClick.emit(this.row);
            },
          },
        ];
      }
    } else if (this.context === 'routing-slip') {
      // Routing slip specific menu items based on user permissions
      this.setupRoutingSlipMenuItems();
    } else {
      // Default menu items based on user role
      this.items = [
        {
          label: 'View',
          icon: 'pi pi-eye',
          command: () => {
            this.onMenuItemClick();
            this.viewClick.emit(this.row);
          },
        },
        {
          label: 'Approve',
          icon: 'pi pi-check-circle',
          command: () => {
            this.onMenuItemClick();
            this.modalType = 'approve';
            this.modalTitle = 'Reason of Approval:';
            this.showModal = true;
          },
        },
        {
          label: 'Reject',
          icon: 'pi pi-times-circle',
          command: () => {
            this.onMenuItemClick();
            this.modalType = 'reject';
            this.modalTitle = 'Reason of Rejection:';
            this.showModal = true;
          },
        },
      ];

      // Add role-specific last option
      if (this.authService.hasRole(UserRole.MANAGER)) {
        this.items.push({
          label: 'Comment',
          icon: 'pi pi-comment',
          command: () => {
            this.onMenuItemClick();
            this.modalType = 'comment';
            this.modalTitle = 'Add New Comment:';
            this.showModal = true;

            this.comment.emit(this.row);
          },
        });
      } else if (
        this.authService.hasAnyRole([UserRole.ADMIN, UserRole.SECRETARY])
      ) {
        this.items.push({
          label: 'Audit Log',
          icon: 'pi pi-list',
          command: () => {
            this.onMenuItemClick();
            this.auditLog.emit(this.row);
          },
        });
      }
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Setup routing slip menu items based on user permissions
   */
  private setupRoutingSlipMenuItems(): void {
    // Get permission for this row
    const permission = this.getPermissionForRow ? this.getPermissionForRow(this.row) : 0;
    
    // Check if user is admin
    const isAdmin = this.authService.hasRole(UserRole.ADMIN);
    
    // Always show View Audit Log and Archive buttons
    this.items = [
      {
        label: 'View Audit Log',
        icon: 'pi pi-list',
        command: () => {
          this.onMenuItemClick();
          this.auditLog.emit(this.row);
        },
      },
      {
        label: 'Archive',
        icon: 'pi pi-trash',
        command: () => {
          this.onMenuItemClick();
          this.delete.emit(this.row);
        },
      },
    ];

    // Get approval status from row data to determine if reject should be disabled
    const approveRejectStatus = this.row?.approveRejectStatus ?? 3; // Default to 3 (pending)
    const isApproved = approveRejectStatus === 1; // 1 = Approved

    // Special handling for Admin users
    if (isAdmin) {
      // Admin gets Reject button only, but disable it if document is approved
      this.items.splice(1, 0, // Insert before Archive button
        {
          label: 'Reject',
          icon: 'pi pi-times-circle',
          disabled: isApproved, // Disable if document is approved
          command: () => {
            if (!isApproved) {
              this.onMenuItemClick();
              this.modalType = 'reject';
              this.modalTitle = 'Reason of Rejection:';
              this.showModal = true;
            }
          },
        }
      );
    }
    // Add permission-based buttons for non-admin users
    else if (permission === 2) { // ApproveReject permission
      // Reuse the approval status check from above (isApproved variable is already defined)
      this.items.splice(1, 0, // Insert before Archive button
        {
          label: 'Comment',
          icon: 'pi pi-comment',
          command: () => {
            this.onMenuItemClick();
            this.modalType = 'comment';
            this.modalTitle = 'Add New Comment:';
            this.showModal = true;
          },
        },
        {
          label: 'Approve',
          icon: 'pi pi-check-circle',
          command: () => {
            this.openRoutingSlipDecision('approve');
          },
        },
        {
          label: 'Reject',
          icon: 'pi pi-times-circle',
          disabled: isApproved, // Disable if document is approved
          command: () => {
            if (!isApproved) {
              this.openRoutingSlipDecision('reject');
            }
          },
        }
      );
    }
    // For permission === 1 (ViewOnly), 3, 4, or permission === 0 (no access), only show Audit Log and Archive
  }

  // ✅ show for all roles
  get canShowMenu(): boolean {
    return !!this.currentUser?.role;
  }

  /** Manager/Secretary run Approve/Reject through DocuSign (matches routing-slip-details). Admin uses direct API. */
  private get useDocuSignForApprovereject(): boolean {
    const isAdmin = this.authService.hasRole(UserRole.ADMIN);
    return (
      !isAdmin &&
      (this.authService.hasRole(UserRole.MANAGER) ||
        this.authService.hasRole(UserRole.SECRETARY))
    );
  }

  /**
   * Primary action label for the Approve/Reject modal. Approval signs via DocuSign.
   * Rejection submits the mandatory comment and does not open DocuSign.
   */
  get modalSubmitButtonLabel(): string {
    if (
      this.context === 'routing-slip' &&
      this.modalType === 'approve' &&
      this.useDocuSignForApprovereject
    ) {
      return 'Sign and Submit';
    }
    return 'Submit';
  }

  /**
   * Routing-slip table and card menus share this path with the details page.
   * A later level is warned here, before the approval note opens.
   */
  private openRoutingSlipDecision(kind: 'approve' | 'reject'): void {
    this.onMenuItemClick();
    const id = this.row?.id;
    if (!id) {
      return;
    }

    this.routingSlipService.getRoutingSlipById(id).subscribe({
      next: (details) => {
        if (
          isEarlierRoutingLevelPending(
            details,
            this.authService.getCurrentUser()
          )
        ) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Please wait',
            detail: WAIT_FOR_PREVIOUS_USER_MESSAGE,
          });
          return;
        }

        this.modalType = kind;
        this.modalTitle =
          kind === 'approve' ? 'Reason of Approval:' : 'Reason of Rejection:';
        this.showModal = true;
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Unable to check approval order. Please try again.',
        });
      },
    });
  }

  /**
   * Handle menu toggle - ensures only one menu is open at a time
   */
  onMenuToggle(event: Event): void {
    event.stopPropagation();

    if (this.isMenuVisible) {
      // Menu is currently visible, close it
      this.closeMenu();
    } else {
      // Recalculate menu items based on current row data (for dynamic disabled state)
      // This ensures the disabled state is always current when menu opens
      if (this.context === 'routing-slip') {
        this.setupRoutingSlipMenuItems();
      }
      // Menu is not visible, open it and close others
      this.openMenu();
    }
  }

  /**
   * Open the menu and set it as active
   */
  private openMenu(): void {
    if (this.menu) {
      this.menu.toggle(event);
      this.isMenuVisible = true;
      this.actionMenuStateService.setActiveMenu(this.menuId);
    }
  }

  /**
   * Close the menu
   */
  private closeMenu(): void {
    if (this.menu && this.isMenuVisible) {
      this.menu.hide();
      this.isMenuVisible = false;
    }
  }

  /**
   * Handle menu hide event
   */
  onMenuHide(): void {
    this.isMenuVisible = false;
    // Clear active menu if this was the active one
    if (this.actionMenuStateService.getActiveMenuId() === this.menuId) {
      this.actionMenuStateService.clearActiveMenu();
    }
  }

  /**
   * Handle clicks outside the menu to close it
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    // Only close if this menu is currently visible and the click is outside
    if (
      this.isMenuVisible &&
      this.actionMenuStateService.getActiveMenuId() === this.menuId
    ) {
      const target = event.target as HTMLElement;
      const menuElement = this.menu?.el?.nativeElement;
      const buttonElement = event.currentTarget as HTMLElement;

      // Check if click is outside both the menu and the button
      if (
        menuElement &&
        !menuElement.contains(target) &&
        !buttonElement.contains(target)
      ) {
        this.closeMenu();
        this.actionMenuStateService.clearActiveMenu();
      }
    }
  }

  /**
   * Handle menu item click - close menu after action
   */
  onMenuItemClick(): void {
    // Close the menu when any menu item is clicked
    this.closeMenu();
    this.actionMenuStateService.clearActiveMenu();
  }

  // Modal methods
  onModalClose(): void {
    this.showModal = false;
  }

  onModalSubmit(data: any): void {
    // Emit the appropriate event based on modal type
    if (this.modalType === 'comment') {
      this.comment.emit({ row: this.row, comment: data.comment });
    } else if (this.modalType === 'approve') {
      this.approve.emit({ row: this.row, comment: data.comment });
    } else if (this.modalType === 'reject') {
      this.reject.emit({ row: this.row, comment: data.comment });
    }
    this.showModal = false;
  }
}
